import { t } from '../helpers/translation';
import { isNil } from 'lodash';
import {
    AdditionalBaggage,
    Baggage,
    BaggageIncluded,
    BaggagePerSector,
    BaggageType,
    IncludedBaggages,
    LegType,
} from '../interfaces/baggage';
import { FlightProduct, IncludedBaggage } from '@/api/Cart/cart.interfaces';
import { Money } from '@libs/money/money.interfaces';

function getBaggageDisplayKey(type: BaggageType): string {
    switch (type) {
        case BaggageType.CabinBag:
            return 'shared.hand_baggage_title';
        case BaggageType.CheckedBag:
            return 'shared.checked_baggage_title';
        default:
            return 'shared.baggage';
    }
}

function getBaggageLegDisplayKey(sectors: BaggagePerSector[] | null): string {
    if (isNil(sectors)) {
        return '';
    }

    const sector1 = sectors?.find((s) => s.SectorNumber === 1);
    const sector2 = sectors?.find((s) => s.SectorNumber === 2);

    if (!isNil(sector1) && isNil(sector2)) {
        return 'shared.outbound_flight';
    } else if (isNil(sector1) && !isNil(sector2)) {
        return 'shared.homebound';
    } else {
        return '';
    }
}

export function getBaggagePrice(price: Money, leg: LegType, isOneWay: boolean) {
    const split = leg === LegType.RoundTrip && !isOneWay;
    const amount = split ? price.Amount / 2 : price.Amount;

    return { Price: { ...price, Amount: amount }, Split: split };
}

export function getSelectedBaggages(baggages: AdditionalBaggage): Baggage[] {
    return baggages?.Alternatives?.filter((x) => x.IsSelected) || [];
}

export function getIncludedBaggages({ Legs }: FlightProduct): IncludedBaggages {
    const isOneWay = Legs[1] === undefined;

    const outboundBags = getIncludedBag(Legs[0]?.IncludedBaggage);
    const inboundBags = !isOneWay ? getIncludedBag(Legs[1]?.IncludedBaggage) : [];

    const isIdentical = JSON.stringify(outboundBags) === JSON.stringify(inboundBags);

    if (isOneWay || isIdentical) {
        return {
            splitSector: false,
            baggages: [...outboundBags].map((bag: BaggageIncluded) => ({
                ...bag,
                PerSector: [{ SectorNumber: 1 }, { SectorNumber: 2 }],
            })),
        };
    } else {
        return {
            splitSector: true,
            baggages: [
                ...outboundBags.map((bag: BaggageIncluded) => ({
                    ...bag,
                    PerSector: [{ SectorNumber: 1 }],
                })),
                ...inboundBags.map((bag: BaggageIncluded) => ({
                    ...bag,
                    PerSector: [{ SectorNumber: 2 }],
                })),
            ],
        };
    }
}

function getIncludedBag(bag: IncludedBaggage): BaggageIncluded[] {
    const bags: BaggageIncluded[] = [];

    if (!bag) {
        return bags;
    }

    const { Cabin, CheckedIn } = bag;

    if (Cabin) {
        const { TotalWeight, Pieces } = Cabin;
        if (TotalWeight || Pieces) {
            bags.push({
                TotalWeight: TotalWeight ?? 0,
                Pieces: Pieces ?? 0,
                Type: BaggageType.CabinBag,
                PerSector: null,
            });
        }
    }

    if (CheckedIn) {
        const { TotalWeight, Pieces } = CheckedIn;
        if (TotalWeight || Pieces) {
            bags.push({
                TotalWeight: TotalWeight ?? 0,
                Pieces: Pieces ?? 0,
                Type: BaggageType.CheckedBag,
                PerSector: null,
            });
        }
    }

    return bags;
}

export function getIconByType(type: BaggageType) {
    switch (type) {
        case BaggageType.SmallBag:
            return 'SmallBag';
        case BaggageType.CabinBag:
            return 'CabinBag';
        case BaggageType.CheckedBag:
            return 'CheckedBag';
        default:
            return null;
    }
}

export function getBaggagesByTypeAndLeg(
    { SplitSector, Alternatives }: AdditionalBaggage,
    type: BaggageType,
    leg: LegType
): Baggage[] {
    return [...Alternatives].filter((x: Baggage) => {
        if (x.Type === type && x.Price?.Amount > 0) {
            const sector1 = x.PerSector?.find((s) => s.SectorNumber === 1);
            const sector2 = x.PerSector?.find((s) => s.SectorNumber === 2);

            if (
                leg === LegType.RoundTrip &&
                ((!SplitSector && !isNil(sector1)) ||
                    (SplitSector && !isNil(sector1) && !isNil(sector2)))
            ) {
                return x;
            }

            if (leg === LegType.Outbound && !isNil(sector1) && isNil(sector2)) {
                return x;
            }

            if (leg === LegType.Inbound && isNil(sector1) && !isNil(sector2)) {
                return x;
            }
        }
    });
}

export function getBaggageLabel(
    bag: Baggage,
    showSector?: boolean,
    showType?: boolean
): string {
    if (!bag) {
        return '';
    }

    const labelWeight = t({ key: 'shared.weight' });
    const labelPiece = t({ key: 'shared.piece_suffix' });

    const labelBag = showType !== false ? t({ key: getBaggageDisplayKey(bag.Type) }) : '';

    const labelLeg = showSector
        ? ` (${t({ key: getBaggageLegDisplayKey(bag.PerSector) })})`
        : '';

    let str = '';
    const { Weight, Pieces } = bag;

    if (Pieces && Weight) {
        str = `${Pieces} x ${Weight} ${labelWeight} ${labelBag}`;
    }
    // If only pieces value
    else if (Pieces && !Weight) {
        str = `${Pieces} ${labelPiece} ${labelBag}`;
    }
    // If only weight value
    else if (!Pieces && Weight) {
        str = `${Weight} ${labelWeight} ${labelBag}`;
    }
    // If no pieces and no weight but bag is included
    else if (!Pieces && !Weight && labelBag) {
        str = `${labelBag}`;
    }

    str += labelLeg;
    return str;
}

export function getIncludedBaggageLabel(
    bag: BaggageIncluded,
    showSector?: boolean,
    showType?: boolean
): string {
    if (!bag) {
        return '';
    }

    const labelWeight = t({ key: 'shared.weight' });
    const labelPiece = t({ key: 'shared.piece_suffix' });

    const labelBag = showType !== false ? t({ key: getBaggageDisplayKey(bag.Type) }) : '';

    const labelLeg = showSector
        ? ` (${t({ key: getBaggageLegDisplayKey(bag.PerSector) })})`
        : '';

    let str = '';
    const { TotalWeight, Pieces } = bag;

    if (Pieces && TotalWeight) {
        const weightPerPiece = TotalWeight / Pieces;
        str = `${Pieces} x ${weightPerPiece} ${labelWeight} ${labelBag}`;
    }
    // If only pieces value
    else if (Pieces && !TotalWeight) {
        str = `${Pieces} ${labelPiece} ${labelBag}`;
    }
    // If only weight value
    else if (!Pieces && TotalWeight) {
        str = ` ${TotalWeight} ${labelWeight} ${labelBag}`;
    }

    str += labelLeg;
    return str;
}

export const filterAdditionalBaggage = (bags: AdditionalBaggage): AdditionalBaggage => {
    return {
        ...bags,
        Alternatives: [...bags.Alternatives].filter((x: Baggage) => {
            // This will be done in BE when we know the new setup works. Leave it for now.
            const hasAmount = x.Price?.Amount > 0;

            const hasSector1 = x.PerSector?.find((x) => x.SectorNumber === 1);

            const hasSector2 = x.PerSector?.find((x) => x.SectorNumber === 2);

            if (hasAmount && !(bags.SplitSector && hasSector1 && hasSector2)) {
                return x;
            }
        }),
    };
};

export const getBaggagesBySector = (baggages: Baggage[], sector: number) =>
    [...baggages].filter((x: Baggage) => x.PerSector?.find((x) => x.SectorNumber === sector));
